import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Badge, Button, IconButton } from '@/components/ui';
import { Empty } from '@/components/ui/EmptyState';
import { XStack } from '@/components/ui/layout/layout';
import {
  CreditPacksSection,
  PlanUsageRows,
  SubscriptionPlan,
  subscriptionApis,
  subscriptionStore,
  UpgradePromptCard,
  UsageBreakdown,
  useCreditBalanceQuery,
} from '@/features/subscription';
import { TierDisplayLabel, type MemberTier } from '@/features/team/team.types';
import { useHashRoute } from '@/hooks/useHashRoute';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';

import { SettingsSection } from '../settings-section';

type UsageAudience = 'internal' | 'team' | 'basic' | 'paid';

const DESCRIPTIONS: Record<UsageAudience, string> = {
  internal:
    'Chats, routines and meeting prep all draw from your monthly usage. Internal accounts are never blocked.',
  team: 'Chats, routines and meeting prep all draw from your monthly usage.',
  basic:
    'Chats, routines and meeting prep all draw from your daily usage. Upgrade for monthly usage that refills.',
  paid: 'Chats, routines and meeting prep all draw from your monthly usage. When it runs out, usage credits are spent next.',
};

const getUsageAudience = (): UsageAudience => {
  if (userStore.isInternalUser) return 'internal';
  if (subscriptionStore.isTeamMember) return 'team';
  if (subscriptionStore.state.currentPlan === SubscriptionPlan.BASIC) {
    return 'basic';
  }
  return 'paid';
};

export const UsageSettings = observer(() => {
  const { setHash } = useHashRoute();
  const { data, isError, isFetching, refetch } = useCreditBalanceQuery();
  const audience = getUsageAudience();
  const team = subscriptionStore.state.team;
  const planLabel =
    audience === 'team' && team
      ? TierDisplayLabel[team.tier as MemberTier]
      : subscriptionStore.currentPlanLabel;
  const isRefreshing = isFetching || subscriptionStore.state.isLoading;

  const refresh = () => Promise.all([refetch(), subscriptionApis.getStatus()]);

  const goToPlans = () => {
    trackEvent('UPGRADE_BUTTON_CLICKED', { source: 'usage_welcome' });
    setHash('settings/Subscription');
  };

  return (
    <SettingsSection.Root>
      <XStack className="items-start justify-between gap-4">
        <SettingsSection.Header>
          <XStack className="items-center gap-2">
            <SettingsSection.Title>Usage</SettingsSection.Title>
            <Badge variant="secondary">{planLabel}</Badge>
          </XStack>
          <SettingsSection.Description>
            {DESCRIPTIONS[audience]}
          </SettingsSection.Description>
        </SettingsSection.Header>
        <IconButton
          size="sm"
          icon={Icons.refresh}
          tooltip="Refresh"
          aria-label="Refresh usage"
          isLoading={isRefreshing}
          onClick={() => refresh()}
        />
      </XStack>
      {isError && !data ? (
        <Empty.Root variant="compact" className="py-16">
          <Empty.Header>
            <Empty.Title className="text-base">
              Could not load your usage
            </Empty.Title>
            <Empty.Description>
              Check your connection and try again.
            </Empty.Description>
          </Empty.Header>
          <Empty.Actions>
            <Button variant="outline" size="sm" onClick={() => refresh()}>
              Try again
            </Button>
          </Empty.Actions>
        </Empty.Root>
      ) : (
        <>
          <SettingsSection.Group title="Your usage">
            <PlanUsageRows />
          </SettingsSection.Group>
          {audience === 'paid' && (
            <SettingsSection.Group title="Usage credits">
              <CreditPacksSection />
            </SettingsSection.Group>
          )}
          {audience === 'basic' && <UpgradePromptCard onUpgrade={goToPlans} />}
          <SettingsSection.Group
            title="Where your usage went"
            trailing={
              <span className="text-xs text-foreground-muted">
                Last 30 days
              </span>
            }
          >
            <UsageBreakdown />
          </SettingsSection.Group>
        </>
      )}
    </SettingsSection.Root>
  );
});
